import { CanAccess } from '@modules/common/components/CanAccess';
import { ConnectedIntegrationChip } from '@modules/integrationsV2/components/ConnectedIntegrationChip';
import { SelectOrAddWorkspaceMenu } from '@modules/integrationsV2/components/SelectOrAddWorkspaceMenu';
import {
  useFetchConnectedIntegrations,
  useIsIntegrationContextEnabled,
  useOnConnectNewIntegration,
} from '@modules/integrationsV2/hooks';
import { useGetConnectedIntegrationWorkspaces } from '@modules/integrationsV2/hooks/useGetConnectedIntegrationWorkspaces';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  getCanAddIntegrationChannelsMetadata,
  getIsCohortIntegrationAlreadyConnected,
  getIsUserOrAccountIntegrationAlreadyConnected,
  getShouldHideConnectCTA,
} from '@modules/integrationsV2/utils';
import {
  AddRounded,
  HelpOutlineRounded,
  KeyboardArrowRightRounded,
} from '@mui/icons-material';
import { Button } from '@mui/material';
import {
  IntegrationChannelViewFragment,
  IntegrationType,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { getInstructionsUrlForChannel } from 'modules/ui/Settings/Integrations/utils/getInstructionsUrlForChannel';
import { useState } from 'react';

interface ConnectIntegrationCTAProps {
  integrationChannel: IntegrationChannelViewFragment;
  integrationType: IntegrationType;
  oAuthURL: string | undefined;
  buttonLabel?: string;
  isDisabled?: boolean;
  isDrawerView?: boolean;
  onClickConnect?: () => void;
}

export const ConnectIntegrationCTA = ({
  integrationChannel,
  integrationType = IntegrationType.Feedback,
  oAuthURL,
  buttonLabel = 'Connect',
  isDisabled = false,
  isDrawerView = true,
  onClickConnect,
}: ConnectIntegrationCTAProps) => {
  const { type: channelType } = integrationChannel;

  const [workspaceMenuAnchorEl, setWorkspaceMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const isIntegrationContextEnabled = useIsIntegrationContextEnabled();

  const { connectedIntegrations } = useFetchConnectedIntegrations({
    fetchPolicy: 'cache-first',
  });

  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);
  const integrationTypeFilterInDrawer = useIntegrationsStore(
    ({ view }) => view.integrationTypeFilterInDrawer,
  );

  const onConnect = useOnConnectNewIntegration({
    integrationChannel,
    oAuthURL,
  });

  const isCohortIntegrationAlreadyConnected =
    getIsCohortIntegrationAlreadyConnected({
      connectedIntegrations,
      channelType,
    });

  const isUserOrAccountIntegrationAlreadyConnected =
    integrationTypeFilterInDrawer !== 'all' &&
    getIsUserOrAccountIntegrationAlreadyConnected({
      connectedIntegrations,
      integrationType: integrationTypeFilterInDrawer,
    });

  const connectedWorkspaces = useGetConnectedIntegrationWorkspaces({
    integrationType,
    channelType: integrationChannel.type,
  });

  const shouldShowAsConnected =
    isCohortIntegrationAlreadyConnected ||
    isUserOrAccountIntegrationAlreadyConnected;

  if (shouldShowAsConnected) {
    return isDrawerView ? (
      <ConnectedIntegrationChip
        channelType={channelType}
        integrationType={
          integrationTypeFilterInDrawer !== 'all'
            ? integrationTypeFilterInDrawer
            : integrationType
        }
      />
    ) : null;
  }

  const helpCenterInstructionsURL =
    getInstructionsUrlForChannel(integrationChannel);

  const shouldHideConnectCTA = getShouldHideConnectCTA(integrationChannel);

  if (shouldHideConnectCTA) {
    return isDrawerView ? (
      <Button
        size="small"
        variant="text"
        onClick={() => {
          if (helpCenterInstructionsURL) {
            window.open(helpCenterInstructionsURL, '_blank');
          }
        }}
        startIcon={<HelpOutlineRounded fontSize="small" color="primary" />}
      >
        See instructions
      </Button>
    ) : null;
  }

  const shouldDirectConnect =
    !isDrawerView || integrationType === IntegrationType.Cohorts;

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClickConnect?.();

    const canAddIntegrationChannelsMetadata =
      getCanAddIntegrationChannelsMetadata(channelType);

    const canOpenWorkspaceMenu = isDrawerView
      ? integrationTypeFilterInDrawer !== 'all'
      : true;

    if (
      isIntegrationContextEnabled &&
      canAddIntegrationChannelsMetadata &&
      canOpenWorkspaceMenu &&
      connectedWorkspaces.length > 0
    ) {
      setWorkspaceMenuAnchorEl(event.currentTarget);
      return;
    }

    if (shouldDirectConnect) {
      onConnect(integrationType);
      return;
    }

    if (integrationTypeFilterInDrawer === 'all') {
      dispatch({
        type: IntegrationsActionType.SET_VIEW,
        payload: {
          selectedIntegrationToConnect: {
            integrationChannel,
            integrationType,
          },
        },
      });
      return;
    }

    onConnect(integrationTypeFilterInDrawer);
  };

  const shouldShowContinueLabel =
    !shouldDirectConnect && integrationTypeFilterInDrawer === 'all';

  return (
    <>
      <CanAccess
        resource={PermissionResource.Integration}
        operation={PermissionOperation.Write}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={shouldShowContinueLabel ? null : <AddRounded />}
          endIcon={
            shouldShowContinueLabel ? <KeyboardArrowRightRounded /> : null
          }
          onClick={handleSubmit}
          disabled={isDisabled}
          sx={{
            whiteSpace: 'nowrap',
            minWidth: 'max-content',
          }}
        >
          {shouldShowContinueLabel ? 'Continue' : buttonLabel}
        </Button>
      </CanAccess>
      <SelectOrAddWorkspaceMenu
        integrationChannel={integrationChannel}
        integrationType={integrationType}
        oAuthURL={oAuthURL}
        anchorEl={workspaceMenuAnchorEl}
        closeMenu={() => setWorkspaceMenuAnchorEl(null)}
        onSelect={(integrationID) => {
          dispatch({
            type: IntegrationsActionType.SET_VIEW,
            payload: {
              selectedIntegrationIDToUpdate: integrationID,
              isAddDrawerOpen: false,
            },
          });
        }}
      />
    </>
  );
};
